import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { setCurrentNote, resetNotesState, getNotesAsync } from '../../../redux/flows/portal/notes'
import { useAppDispatch, type RootState } from '../../../redux/store'

/**
 * NotesScreen hook
 *
 * Responsibilities:
 * 1. Initialize first note when pending notes are loaded
 * 2. Reset state when pending notes list is empty
 * 3. Reset state on component unmount
 */

export const useNotesScreen = () => {
  const dispatch = useAppDispatch()

  // ===== REDUX STATE =====
  const pendingNotes = useSelector((state: RootState) => state.notesPortal.notes)
  const notesStatus = useSelector((state: RootState) => state.notesPortal.notesStatus)
  const currentNote = useSelector((state: RootState) => state.notesPortal.currentNote)
  const historySelectedNoteId = useSelector((state: RootState) => state.notesPortal.historySelectedNoteId)
  const clinicId = useSelector((state: RootState) => state.studioAuth.studioDetails?.id)

  // ===== INITIALIZATION =====

  /**
   * When pending notes are loaded and no note is selected,
   * automatically select the first note and fetch patient history
   * This ensures the first pending note is always selected by default
   */
  useEffect(() => {
    if (pendingNotes.length > 0 && notesStatus === 'fulfilled') {
      const currentNoteInList = currentNote && pendingNotes.find((note) => note.id === currentNote.id)
      // Only auto-select first pending when nothing is selected AND no history note is selected
      // This prevents overwriting selection when user is viewing a history note
      if (!currentNoteInList && !historySelectedNoteId) {
        const firstNote = pendingNotes[0]
        dispatch(setCurrentNote(firstNote))
        // Fetch patient notes history for the first note
        if (firstNote.patient?.id) {
          dispatch(getNotesAsync(firstNote.patient.id))
        }
      }
    }
  }, [pendingNotes, currentNote, historySelectedNoteId, notesStatus, dispatch])

  // ===== RESET LOGIC =====

  /**
   * Reset all notes state when pending notes list is empty
   */
  useEffect(() => {
    if (pendingNotes.length === 0) {
      dispatch(resetNotesState())
    }
  }, [pendingNotes.length, dispatch])

  /**
   * Reset all notes state on component unmount
   */
  useEffect(() => {
    return () => {
      dispatch(resetNotesState())
    }
  }, [dispatch])

  // ===== RETURN VALUES =====

  return {
    pendingNotes,
    notesStatus,
    currentNote,
    clinicId,
  }
}
